import { z } from "zod";
import { isCalendarDate, isClockTime } from "./contracts";

/**
 * Validação do domínio Financeiro.
 *
 * Este é o ÚNICO lugar onde a forma de cada intenção é decidida: a interface, o
 * adaptador demo e o repositório remoto usam estes schemas, e a RPC repete as
 * mesmas regras no banco. Validação divergente entre camadas é o defeito que a
 * seção 14.1 do AGENTS proíbe.
 */
const calendarDate = z
  .string()
  .refine(isCalendarDate, "Informe uma data válida no formato dia/mês/ano.");

const clockTime = z.string().refine(isClockTime, "Confira o horário do recebimento.");

/** Centavos: inteiro seguro e positivo. Zero não é cobrança. */
const positiveCents = z
  .number()
  .int("O valor deve ser um número inteiro de centavos.")
  .positive("Informe um valor maior que zero.")
  .max(Number.MAX_SAFE_INTEGER);

export const contractDraftSchema = z
  .object({
    leadId: z.string().trim().min(1, "Lead é obrigatório."),
    membershipId: z.string().trim().min(1).optional(),
    kind: z.enum(["monthly", "oneoff"]),
    description: z.string().trim().min(1, "Descreva o contrato.").max(200),
    amountCents: positiveCents,
    startOn: calendarDate,
    firstDueOn: calendarDate,
    endOn: calendarDate.optional(),
    sourceSnapshot: z.string().trim().max(120).optional(),
  })
  .superRefine((draft, ctx) => {
    if (draft.firstDueOn < draft.startOn) {
      ctx.addIssue({
        code: "custom",
        path: ["firstDueOn"],
        message: "O primeiro vencimento não pode ser anterior ao início do contrato.",
      });
    }
    if (draft.endOn && draft.endOn < draft.firstDueOn) {
      ctx.addIssue({
        code: "custom",
        path: ["endOn"],
        message: "O término não pode ser anterior ao primeiro vencimento.",
      });
    }
  });

export type ContractDraft = z.output<typeof contractDraftSchema>;
export type ContractDraftInput = z.input<typeof contractDraftSchema>;

export const generateInvoicesSchema = z.object({
  contractId: z.string().trim().min(1, "Contrato é obrigatório."),
  /** Gera parcelas com vencimento até esta data, inclusive. */
  until: calendarDate,
});

export type GenerateInvoicesInput = z.infer<typeof generateInvoicesSchema>;

export const extraInvoiceSchema = z.object({
  contractId: z.string().trim().min(1, "Contrato é obrigatório."),
  description: z.string().trim().min(1, "Descreva o extra.").max(200),
  amountCents: positiveCents,
  dueOn: calendarDate,
});

export type ExtraInvoiceInput = z.infer<typeof extraInvoiceSchema>;

export const receiptSchema = z.object({
  invoiceId: z.string().trim().min(1, "Parcela é obrigatória."),
  amountCents: positiveCents,
  receivedOn: calendarDate,
  receivedAtTime: clockTime.default("12:00"),
});

export type ReceiptInput = z.input<typeof receiptSchema>;
export type ReceiptDraft = z.output<typeof receiptSchema>;

export const cancelContractSchema = z.object({
  contractId: z.string().trim().min(1, "Contrato é obrigatório."),
  cancelledOn: calendarDate,
});

export type CancelContractInput = z.infer<typeof cancelContractSchema>;

export const revenueGoalSchema = z
  .object({
    periodStart: calendarDate,
    periodEnd: calendarDate,
    amountCents: positiveCents,
  })
  .superRefine((goal, ctx) => {
    if (goal.periodEnd < goal.periodStart) {
      ctx.addIssue({
        code: "custom",
        path: ["periodEnd"],
        message: "O fim do período não pode ser anterior ao início.",
      });
    }
  });

export type RevenueGoalInput = z.infer<typeof revenueGoalSchema>;

/**
 * Converte texto digitado em centavos.
 *
 * Aceita "1.234,56", "1234.56" e "1234". Devolve `null` quando o texto não
 * descreve um valor positivo — a interface mostra o erro do campo em vez de
 * gravar um número errado.
 */
export function parseCents(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const hasComma = trimmed.includes(",");
  const normalized = hasComma
    ? trimmed.replace(/\./g, "").replace(",", ".")
    : trimmed.replace(/(?<=\d)\.(?=\d{3}\b)/g, "");

  const numeric = Number(normalized.replace(/[^\d.-]/g, ""));
  if (!Number.isFinite(numeric)) return null;

  const cents = Math.round(numeric * 100);
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export function formatCents(cents: number): string {
  return BRL.format(cents / 100);
}
